import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { SwiperProps } from '@components/atoms/swiper/types';
import type { ThemeProps } from '@models/types';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import styled from 'styled-components';
import { Autoplay, Pagination } from 'swiper/modules';
import type { SwiperClass } from 'swiper/react';
import { Swiper as ReactSwiper } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';

export { SwiperSlide } from 'swiper/react';

const Atom: FC<SwiperProps> = ({ children, className, indicatorVariant = 'progress', ...rest }) => {
    const [swiper, setSwiper] = useState<SwiperClass | null>(null);
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const [currentProgress, setCurrentProgress] = useState<number>(0);
    const theme = useTheme<ThemeProps>();
    const progressRef = useRef<number>(0);

    useEffect(() => {
        if (!swiper) return;
        if (indicatorVariant !== 'progress') return;
        const interval = setInterval(() => {
            setCurrentProgress((prev) => {
                const newProgress = prev + 0.01;

                progressRef.current = newProgress;

                return newProgress >= 1 ? 1 : newProgress; // Не сбрасываем до 0
            });
        }, rest?.autoplay?.delay / 100 || 100);

        // eslint-disable-next-line consistent-return
        return () => clearInterval(interval);
    }, [rest?.autoplay?.delay, swiper, indicatorVariant]);

    const getWidthPercentage = (index: number, current: number, progress: number) => {
        if (index < current) {
            return 100;
        }
        if (index === current) {
            return progress * 100;
        }

        return 0;
    };

    useEffect(() => {
        if (indicatorVariant === 'progress') return;
        if (currentProgress >= 1) {
            swiper?.slideNext();
        }
    }, [currentProgress, swiper, indicatorVariant]);

    return (
        <Box className={className}>
            {indicatorVariant === 'progress' && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                        mb: 3,
                    }}
                >
                    {swiper?.slides?.map((_slide: SwiperClass['slides'][number], index: number) => (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '64px',
                                height: '4px',
                                borderRadius: '40px',
                                backgroundColor: theme.palette.primaryA5,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                '&:hover': {
                                    transition: 'all 0.3s',
                                    opacity: 0.5,
                                },
                            }}
                            key={index.toString()}
                            onClick={() => {
                                swiper.slideTo(index);
                                setCurrentSlide(index);
                                setCurrentProgress(0);
                                progressRef.current = 0;
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    width: `${getWidthPercentage(
                                        index,
                                        currentSlide,
                                        currentProgress,
                                    )}%`,
                                    backgroundColor: theme.palette.textLight,
                                    transition: 'width 0.1s linear',
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            )}
            <ReactSwiper
                {...rest}
                modules={[Pagination, Autoplay]}
                onSwiper={setSwiper}
                onActiveIndexChange={(instance: SwiperClass) => {
                    const newIndex = instance.activeIndex;

                    setCurrentSlide(newIndex);
                    setCurrentProgress(0);
                    progressRef.current = 0;
                }}
            >
                {children}
            </ReactSwiper>
            {indicatorVariant === 'dots' && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        ml: 3,
                    }}
                >
                    {swiper?.slides?.map((_slide: SwiperClass['slides'][number], index: number) => (
                        <Box
                            key={index.toString()}
                            onClick={() => swiper.slideTo(index)}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                backgroundColor:
                                    index === currentSlide
                                        ? theme.palette.primaryA1
                                        : theme.palette.primaryA3,
                                opacity: index === currentSlide ? 1 : 0.5,
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export const Swiper = styled(Atom)({});
